import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { IdentityInfo, AWS_ACCOUNTS, getAccountAlias, getAccountEnvironment } from '@/lib/types'
import { AppliedFilters } from '@/pages/DomainsPage'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DeleteDomainDialog } from './delete-dialog'
import { Eye, Trash2, Search, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'

interface DomainTableProps {
  domains: IdentityInfo[]
  total: number
  currentPage: number
  totalPages: number
  isRefreshing?: boolean
  appliedFilters: AppliedFilters
  onRefresh: () => void
  onApplyFilters: (filters: AppliedFilters) => void
  onPageChange: (page: number) => void
}

const statusColors: Record<string, string> = {
  SUCCESS: 'bg-teal-500/15 text-teal-700 hover:bg-teal-500/25',
  PENDING: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  FAILED: 'bg-rose-500/15 text-rose-700 hover:bg-rose-500/25',
  TEMPORARY_FAILURE: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  NOT_STARTED: 'bg-zinc-500/15 text-zinc-700 hover:bg-zinc-500/25',
}

const dkimStatusColors: Record<string, string> = {
  SUCCESS: 'bg-teal-500/15 text-teal-700 hover:bg-teal-500/25',
  PENDING: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  FAILED: 'bg-rose-500/15 text-rose-700 hover:bg-rose-500/25',
  TEMPORARY_FAILURE: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  NOT_STARTED: 'bg-zinc-500/15 text-zinc-700 hover:bg-zinc-500/25',
}

const STATUS_OPTIONS = ['SUCCESS', 'PENDING', 'FAILED', 'TEMPORARY_FAILURE', 'NOT_STARTED']

function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: (number | '...')[] = []
  const addPage = (p: number) => { if (!pages.includes(p)) pages.push(p) }
  const addEllipsis = () => { if (pages[pages.length - 1] !== '...') pages.push('...') }

  addPage(1)
  if (current > 3) addEllipsis()
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) addPage(p)
  if (current < total - 2) addEllipsis()
  addPage(total)

  return pages
}

export function DomainTable({
  domains,
  total,
  currentPage,
  totalPages,
  isRefreshing = false,
  appliedFilters,
  onRefresh,
  onApplyFilters,
  onPageChange,
}: DomainTableProps) {
  const [searchInput, setSearchInput] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState<{ domain: string; accountId: string } | null>(null)

  // Sync local state with applied filters when they change (after successful search)
  useEffect(() => {
    setSearchInput(appliedFilters.search)
    setAccountFilter(appliedFilters.accountId)
    setStatusFilter(appliedFilters.status)
  }, [appliedFilters])

  const handleSearch = () => {
    onApplyFilters({
      search: searchInput,
      accountId: accountFilter,
      status: statusFilter,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClearSearch = () => {
    setSearchInput('')
    onApplyFilters({
      search: '',
      accountId: accountFilter,
      status: statusFilter,
    })
  }

  const handleStatusChange = (value: string | null) => {
    if (value) {
      setStatusFilter(value)
      onApplyFilters({
        search: searchInput,
        accountId: accountFilter,
        status: value,
      })
    }
  }

  const handleDeleteClick = (domain: string, accountId: string) => {
    setSelectedDomain({ domain, accountId })
    setDeleteDialogOpen(true)
  }

  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false)
    setSelectedDomain(null)
    onRefresh()
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Domains</h1>
            <p className="text-sm text-muted-foreground">
              Showing {domains.length} of {total} domains
            </p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search domains..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-9"
          />
          {searchInput && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
              onClick={handleClearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Select value={accountFilter || 'all'} onValueChange={(value) => {
          if (!value) return
          const newValue = value === 'all' ? '' : value
          setAccountFilter(newValue)
          onApplyFilters({
            search: searchInput,
            accountId: newValue,
            status: statusFilter,
          })
        }}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Accounts">
              {accountFilter ? getAccountAlias(accountFilter) : 'All Accounts'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Accounts</SelectItem>
            {AWS_ACCOUNTS.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.alias}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={handleSearch}>
          <Search className="mr-2 h-4 w-4" />
          Search
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Domain</TableHead>
              <TableHead>Account ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>DKIM Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {domains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No domains found
                </TableCell>
              </TableRow>
            ) : (
              domains.map((domain) => (
                <TableRow key={`${domain.account_id}-${domain.domain}`}>
                  <TableCell className="font-medium">{domain.domain || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <Badge
                        variant="outline"
                        className={
                          getAccountEnvironment(domain.account_id) === 'prod'
                            ? 'bg-emerald-500/15 text-emerald-700 border-emerald-300'
                            : getAccountEnvironment(domain.account_id) === 'staging'
                            ? 'bg-amber-500/15 text-amber-700 border-amber-300'
                            : ''
                        }
                      >
                        {getAccountAlias(domain.account_id)}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono mt-0.5">{domain.account_id}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[domain.verification_status] || ''}>
                      {domain.verification_status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={dkimStatusColors[domain.dkim_status] || ''}>
                      {domain.dkim_status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(domain.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/dashboard/domains/${encodeURIComponent(domain.domain)}`}>
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(domain.domain, domain.account_id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isRefreshing}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {buildPageNumbers(currentPage, totalPages).map((item, idx) =>
            item === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">…</span>
            ) : (
              <Button
                key={item}
                variant={item === currentPage ? 'default' : 'outline'}
                size="icon"
                disabled={isRefreshing}
                onClick={() => onPageChange(item as number)}
              >
                {isRefreshing && item === currentPage
                  ? <RefreshCw className="h-3 w-3 animate-spin" />
                  : item}
              </Button>
            )
          )}

          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isRefreshing}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <DeleteDomainDialog
        domain={selectedDomain?.domain ?? null}
        accountId={selectedDomain?.accountId ?? null}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  )
}
